import { CountyProjectContext } from "@app/projects/CountyProjectContext";
import {
  ProjectRunConfigurationType,
  RegionSetStatusType,
} from "@app/projects/enumerators";
import {
  ClaimsActivityLineOfBusinessTypeEnum,
} from "@app/projects/network-assessment/pci-dashboard/claims-activity";
import {
  PciDashboardSpecialty,
} from "@app/projects/network-assessment/pci-dashboard/shared";
import {
  NewPatientClaimsEnum,
  TopCondition,
  TopProcedure,
  TopProcedureFilter
} from "@app/projects/servicing-provider";
import MultiselectDropdown from "@app/shared/checkbox-dropdown/MultiselectDropdown";
import MultiselectNestedDropdown from "@app/shared/checkbox-dropdown/MultiselectNestedDropdown";
import { DropdownItem } from "@app/shared/checkbox-dropdown/types";
import { QESButton } from "@app/shared/qes-standards/Button";
import { QESSelect } from "@app/shared/qes-standards/forms/Select";
import { isEqual } from "lodash";
import React, { JSX, useContext, useEffect, useState } from "react";
import NewPatientClaimsFilter from "@app/projects/servicing-provider/provider-claims/NewPatientClaimsFilter";
import TopConditionFilter from "@app/projects/servicing-provider/provider-claims/TopConditionFilter";
import ClaimsActivityLineOfBusinessFilter from "@app/projects/network-assessment/pci-dashboard/claims-activity/ClaimsActivityLineOfBusinessFilter";
import { PciDashboardFiltersContext } from "@app/projects/network-assessment/pci-dashboard/shared/PciDashboardFiltersContext";
import PciDashboardFiltersOverview from "@app/projects/network-assessment/pci-dashboard/shared/PciDashboardFiltersOverview";
import { PciDashboardFiltersPayload } from "@app/projects/network-assessment/pci-dashboard/shared/PciDashboardFiltersPayload";
import PciDashboardScenarioFilters from "@app/projects/network-assessment/pci-dashboard/shared/PciDashboardScenarioFilters";

interface PciDashboardFiltersProps {
  isLoading: boolean;
  isPca: boolean;
  onApplyFiltersClick: (filtersPayload: PciDashboardFiltersPayload) => void;
  showLineOfBusiness: boolean;
}

const PciDashboardFilters = (props: PciDashboardFiltersProps): JSX.Element => {
  const [hasFilterData, setHasFilterData] = useState<boolean>(false);
  const [isFirstLoad, setIsFirstLoad] = useState<boolean>(true);

  const pciDashboardFiltersContext = useContext(PciDashboardFiltersContext);
  const projectContext = useContext(CountyProjectContext);

  const [initialDataLoaded, setInitialDataLoaded] = useState<boolean>(false);

  useEffect(() => {
    if (props.showLineOfBusiness) {
      pciDashboardFiltersContext.setSelectedLineOfBusiness(
        ClaimsActivityLineOfBusinessTypeEnum.Total,
      );
    }
  }, [props.showLineOfBusiness]);

  useEffect(() => {
    // the project run configuration filter is not currently part of the filters bar
    // changing it will trigger an update to all the widget tiles
    if (
      !!pciDashboardFiltersContext.projectRunConfiguration
        ?.projectRunConfigurationId &&
      (!props.isPca ||
        (!!pciDashboardFiltersContext.selectedLineOfBusiness &&
          !initialDataLoaded))
    ) {
      onApplyFiltersClick();
      setInitialDataLoaded(true);
    }
  }, [
    pciDashboardFiltersContext.projectRunConfiguration,
    pciDashboardFiltersContext.selectedLineOfBusiness,
  ]);

  useEffect(() => {
    if (
      !!pciDashboardFiltersContext.projectRunConfiguration
        ?.projectRunConfigurationId &&
      initialDataLoaded
    ) {
      onApplyFiltersClick();
    }
  }, [pciDashboardFiltersContext.projectRunConfiguration]);

  useEffect(() => {
    if (
      pciDashboardFiltersContext.serviceAreas.length === 0 ||
      pciDashboardFiltersContext.specialties.length === 0
    ) {
      setHasFilterData(false);
    } else {
      setHasFilterData(true);
    }
  }, [
    pciDashboardFiltersContext.serviceAreas,
    pciDashboardFiltersContext.specialties,
  ]);

  const isLoading = (): boolean => {
    return props.isLoading || pciDashboardFiltersContext.filtersLoading;
  };

  const isApplyDisabled = (): boolean => {
    // do not allow button interactions if ANYTHING is loading
    if (isLoading()) {
      return true;
    }

    const countiesEqual = isEqual(
      pciDashboardFiltersContext.displayedFilters?.countySSAs.sort((a, b) =>
        a.localeCompare(b),
      ),
      pciDashboardFiltersContext.selectedCounties.sort((a, b) =>
        a.localeCompare(b),
      ),
    );

    const countyStatusesEqual = isEqual(
      pciDashboardFiltersContext.displayedFilters?.countyStatuses?.sort(
        (a, b) => a.localeCompare(b),
      ),
      pciDashboardFiltersContext.selectedCountyStatuses
        .map((x) => x.key)
        .sort((a, b) => a.localeCompare(b)),
    );

    const lineOfBusinessEqual = isEqual(
      pciDashboardFiltersContext.displayedFilters?.lineOfBusiness,
      pciDashboardFiltersContext.selectedLineOfBusiness,
    );

    const newPatientClaimsFiltersEqual = isEqual(
      pciDashboardFiltersContext.displayedFilters?.newPatientClaims?.sort(
        (a, b) => a.localeCompare(b),
      ),
      pciDashboardFiltersContext.selectedNewPatientClaimsFilters
        .map((x) => x.key)
        .sort((a, b) => a.localeCompare(b)),
    );

    const specialtiesEqual = isEqual(
      pciDashboardFiltersContext.displayedFilters?.specialtyGroupCodes?.sort(
        (a, b) => a.localeCompare(b),
      ),
      pciDashboardFiltersContext.selectedSpecialties
        .map((x) => x.key.specialtyGroupCode)
        .sort((a, b) => a.localeCompare(b)),
    );

    const topProceduresDirty =
      pciDashboardFiltersContext.topProcedures.length > 0 &&
      !isEqual(
        pciDashboardFiltersContext.displayedFilters?.topProcedures?.sort(
          (a, b) => a.localeCompare(b),
        ),
        pciDashboardFiltersContext.selectedTopProcedures
          .map((x) => x.key.code)
          .sort((a, b) => a.localeCompare(b)),
      );

    const topConditionsDirty =
      pciDashboardFiltersContext.topConditions.length > 0 &&
      !isEqual(
        pciDashboardFiltersContext.displayedFilters?.topConditions?.sort(
          (a, b) => a.localeCompare(b),
        ),
        pciDashboardFiltersContext.selectedTopConditions
          .map((x) => x.key.code)
          .sort((a, b) => a.localeCompare(b)),
      );

    const scenariosEqual =
      isEqual(
        pciDashboardFiltersContext.displayedFilters?.scenario,
        pciDashboardFiltersContext.selectedScenario,
      ) &&
      isEqual(
        pciDashboardFiltersContext.displayedFilters?.compareScenario,
        pciDashboardFiltersContext.selectedCompareScenario,
      );

    // if the "compare timeslices"" checkbox is checked, both timeslice dropdowns must have values and must not be equal to each other
    const scenariosValid =
      !pciDashboardFiltersContext.compareTimeslices ||
      (pciDashboardFiltersContext.compareTimeslices &&
        !!pciDashboardFiltersContext.selectedScenario &&
        !!pciDashboardFiltersContext.selectedCompareScenario);

    // in order to avoid unnecessary api calls, prevent the user from applying filters if
    // a.) the currently-selected filters are the same as those that were used for what is already displayed
    // b.) at least one county, one specialty, and a line of business (if shown) are not selected
    if (
      (!countiesEqual ||
        !countyStatusesEqual ||
        !specialtiesEqual ||
        !lineOfBusinessEqual ||
        !newPatientClaimsFiltersEqual ||
        !scenariosEqual ||
        topProceduresDirty ||
        topConditionsDirty) &&
      pciDashboardFiltersContext.selectedCounties.length > 0 &&
      /// only require at least one county status to be selected when the county status feature flag is turned on and there are county status options present
      ((!!pciDashboardFiltersContext.selectedCountyStatuses.length &&
        !!pciDashboardFiltersContext.countyStatuses.length) ||
        !pciDashboardFiltersContext.countyStatuses.length) &&
      (pciDashboardFiltersContext.selectedSpecialties.length > 0) &&
      ((props.showLineOfBusiness &&
        !!pciDashboardFiltersContext.selectedLineOfBusiness) ||
        !props.showLineOfBusiness) &&
      scenariosValid &&
      (!props.isPca ||
        !!pciDashboardFiltersContext.selectedNewPatientClaimsFilters.length ||
        (pciDashboardFiltersContext.selectedNewPatientClaimsFilters.length == 0 &&
          pciDashboardFiltersContext.displayedFilters.newPatientClaims.length == 0))
    ) {
      return false;
    }

    return true;
  };

  /**
   * Is the "Clear" button disabled?
   * @returns true if the "Clear" button should be disabled, false otherwise
   */
  const isClearDisabled = (): boolean => {
    // restoring filters to their defaults is unnecessary if the currently-selected filters are already the defaults

    const disabled = isLoading();

    const countiesDirty =
      pciDashboardFiltersContext.selectedCounties.length !==
      pciDashboardFiltersContext.counties.length;

    const specialtiesDirty =
      pciDashboardFiltersContext.selectedSpecialties.length !==
      pciDashboardFiltersContext.specialties.length;

    const proceduresDirty =
      pciDashboardFiltersContext.selectedTopProcedures.length > 0 &&
      pciDashboardFiltersContext.topProcedures.length > 0;

    const topConditionsDirty =
      pciDashboardFiltersContext.selectedTopConditions.length > 0 &&
      pciDashboardFiltersContext.topConditions.length > 0;

    const lobDirty =
      props.showLineOfBusiness &&
      pciDashboardFiltersContext.selectedLineOfBusiness !==
      ClaimsActivityLineOfBusinessTypeEnum.Total;

    const newPatientClaimsDirty =
      props.isPca &&
      pciDashboardFiltersContext.selectedNewPatientClaimsFilters.length !==
      pciDashboardFiltersContext.newPatientClaimsFilters.length;

    const countyStatusesDirty =
      !!pciDashboardFiltersContext.selectedCountyStatuses
        .map((x) => x.key)
        .includes(RegionSetStatusType.Opportunity) ||
      pciDashboardFiltersContext.selectedCountyStatuses.filter(
        (x) => x.key !== RegionSetStatusType.Opportunity,
      ).length !==
        pciDashboardFiltersContext.countyStatuses.filter(
          (x) => x.key !== RegionSetStatusType.Opportunity,
        ).length;

    const enabled =
      countiesDirty ||
      specialtiesDirty ||
      proceduresDirty ||
      topConditionsDirty ||
      lobDirty ||
      newPatientClaimsDirty ||
      countyStatusesDirty ||
      pciDashboardFiltersContext.compareTimeslices ||
      !!pciDashboardFiltersContext.selectedScenario ||
      !!pciDashboardFiltersContext.selectedCompareScenario;

    return disabled || !enabled;
  };

  const onApplyFiltersClick = (): void => {
    const filtersPayload: PciDashboardFiltersPayload =
      pciDashboardFiltersContext.getFiltersPayload();

    filtersPayload.topProcedures =
      pciDashboardFiltersContext.selectedTopProcedures.length ===
      pciDashboardFiltersContext.topProcedures.length
        ? []
        : filtersPayload.topProcedures;

    filtersPayload.topConditions =
      pciDashboardFiltersContext.selectedTopConditions.length ===
      pciDashboardFiltersContext.topConditions.length
        ? []
        : filtersPayload.topConditions;

    props.onApplyFiltersClick(filtersPayload);

    // onApplyFiltersClick is executed on initial load, however,
    // we will only want to set the displayed filters here if it's not the initial load,
    // since they will be set after the filters are retrieved from the api in this case
    if (isFirstLoad) {
      setIsFirstLoad(false);
    } else {
      pciDashboardFiltersContext.setDisplayedFilters(filtersPayload);
    }
  };

  const getDualMembershipAliases = (): DropdownItem<string>[] => {
    const dualMembershipAliases: DropdownItem<string>[] = [];

    if (
      !!projectContext?.runConfiguration &&
      !!projectContext?.project?.projectRunConfigurations
    ) {
      const companionRunConfiguration =
        projectContext.project.projectRunConfigurations.find(
          (config) =>
            config.projectRunConfigurationId ===
            projectContext.runConfiguration.companionProjectRunConfigurationId,
        );

      const runConfigurationAlias: DropdownItem<string> = {
        key: projectContext.runConfiguration.projectRunConfigurationType,
        name: projectContext.runConfiguration.memberSetAlias,
      };

      const companionRunConfigurationAlias: DropdownItem<string> = {
        key: companionRunConfiguration.projectRunConfigurationType,
        name: companionRunConfiguration.memberSetAlias,
      };

      if (
        projectContext.runConfiguration.projectRunConfigurationType ===
        ProjectRunConfigurationType.DualMembershipPrimary
      ) {
        dualMembershipAliases.push(runConfigurationAlias);
        dualMembershipAliases.push(companionRunConfigurationAlias);
      } else if (
        companionRunConfiguration?.projectRunConfigurationType ===
        ProjectRunConfigurationType.DualMembershipPrimary
      ) {
        dualMembershipAliases.push(companionRunConfigurationAlias);
        dualMembershipAliases.push(runConfigurationAlias);
      }
    }

    return dualMembershipAliases;
  };

  /**
   * Is the Search section visible?
   * @returns true if the search section should be visible, false otherwise
   */
  const isSearchSectionVisible = () =>
    (pciDashboardFiltersContext.topProcedures.length > 0) ||
    (pciDashboardFiltersContext.topConditions.length > 0);

  return (
    <React.Fragment>
      <div className="row">
        <div className="col-12">
          <div className="card card-static">
            <div className="card-body">
              <div className="row">
                <div className="col-12">
                  <label className="label-m mb-2">Filters</label>
                </div>
              </div>
              <div className="row">
                {props.showLineOfBusiness && (
                  <div className="col-md-2">
                    <ClaimsActivityLineOfBusinessFilter
                      disabled={
                        pciDashboardFiltersContext.filtersLoading ||
                        !hasFilterData
                      }
                      selectedLineOfBusiness={
                        pciDashboardFiltersContext.selectedLineOfBusiness
                      }
                      setSelectedLineOfBusiness={
                        pciDashboardFiltersContext.setSelectedLineOfBusiness
                      }
                      testId="pci-dashboard__filters--line-of-business"
                      project={projectContext.project}
                    />
                  </div>
                )}

                <MultiselectNestedDropdown
                  customSelectInputText={
                    pciDashboardFiltersContext.counties.length ===
                    pciDashboardFiltersContext.selectedCounties.length
                      ? "All"
                      : null
                  }
                  disabled={
                    pciDashboardFiltersContext.filtersLoading || !hasFilterData
                  }
                  items={pciDashboardFiltersContext.serviceAreas}
                  label="Service Area"
                  testId="pci-dashboard__filters--service-area"
                  containerClassName="col-md-2"
                  selectedItems={pciDashboardFiltersContext.selectedCounties}
                  onSelectionChanged={(items) =>
                    pciDashboardFiltersContext.setSelectedCounties(items)
                  }
                />
                {!!pciDashboardFiltersContext.countyStatuses.length && (
                  <MultiselectDropdown
                    containerClassName="col-md-2"
                    customSelectInputText={
                      pciDashboardFiltersContext.countyStatuses.length ===
                      pciDashboardFiltersContext.selectedCountyStatuses.length
                        ? "All"
                        : null
                    }
                    disabled={pciDashboardFiltersContext.filtersLoading}
                    items={pciDashboardFiltersContext.countyStatuses}
                    onSelectionChanged={(
                      selectedCountyStatuses: DropdownItem<RegionSetStatusType>[],
                    ) => {
                      pciDashboardFiltersContext.setSelectedCountyStatuses(
                        selectedCountyStatuses,
                      );
                    }}
                    label="County Status"
                    selectedItems={
                      pciDashboardFiltersContext.selectedCountyStatuses
                    }
                    testId="pci-dashboard__filters--county-status"
                  />
                )}
                <MultiselectDropdown
                  containerClassName="col-md-3"
                  customSelectInputText={
                    pciDashboardFiltersContext.specialties.length ===
                    pciDashboardFiltersContext.selectedSpecialties.length
                      ? "All"
                      : null
                  }
                  disabled={
                    pciDashboardFiltersContext.filtersLoading || !hasFilterData
                  }
                  items={pciDashboardFiltersContext.specialties}
                  onSelectionChanged={(
                    selectedSpecialties: DropdownItem<PciDashboardSpecialty>[],
                  ) => {
                    pciDashboardFiltersContext.setSelectedSpecialties(
                      selectedSpecialties,
                    );
                  }}
                  label="Specialty"
                  selectedItems={pciDashboardFiltersContext.selectedSpecialties}
                  showTooltipOnOverflow={true}
                  testId="pci-dashboard__filters--specialty"
                />

                {props.isPca &&
                  !!pciDashboardFiltersContext.newPatientClaimsFilters.length && (
                    <div className="col-md-2">
                      <NewPatientClaimsFilter
                        title="New Patient Claims"
                        disabled={
                          pciDashboardFiltersContext.filtersLoading ||
                          !hasFilterData
                        }
                        testId="pci-dashboard__filters--new-patient-claims-filters"
                        selectedValues={
                          pciDashboardFiltersContext.selectedNewPatientClaimsFilters
                        }
                        mappedValues={
                          pciDashboardFiltersContext.newPatientClaimsFilters
                        }
                        setSelectedValues={(
                          selectedNewPatientClaimsFilters: DropdownItem<NewPatientClaimsEnum>[],
                        ) => {
                          pciDashboardFiltersContext.setSelectedNewPatientClaimsFilters(
                            selectedNewPatientClaimsFilters,
                          );
                        }}
                      />
                    </div>
                  )}
              </div>
              {isSearchSectionVisible() && (
                <div className="row">
                  <div className="col-12 mt-3">
                    <label className="label-m mb-2">Search</label>
                  </div>
                </div>
              )}
              <div className="row">
                {pciDashboardFiltersContext.topProcedures.length > 0 && (
                  <TopProcedureFilter
                    containerClassName="col-md-5"
                    disabled={
                      pciDashboardFiltersContext.filtersLoading ||
                      !hasFilterData
                    }
                    topProcedures={pciDashboardFiltersContext.topProcedures}
                    onSelectionChanged={(
                      selectedTopProcedures: DropdownItem<TopProcedure>[],
                    ) => {
                      pciDashboardFiltersContext.setSelectedTopProcedures(
                        selectedTopProcedures,
                      );
                    }}
                    selectedTopProcedures={
                      pciDashboardFiltersContext.selectedTopProcedures
                    }
                    setSelectedTopProcedures={
                      pciDashboardFiltersContext.setSelectedTopProcedures
                    }
                    testId="pci-dashboard__filters--top-procedures"
                    title="Procedures Search"
                  />
                )}
                {pciDashboardFiltersContext.topConditions.length > 0 && (
                    <TopConditionFilter
                      containerClassName="col-md-5"
                      disabled={
                        pciDashboardFiltersContext.filtersLoading ||
                        !hasFilterData
                      }
                      topConditions={pciDashboardFiltersContext.topConditions}
                      onSelectionChanged={(
                        selectedTopConditions: DropdownItem<TopCondition>[],
                      ) => {
                        pciDashboardFiltersContext.setSelectedTopConditions(
                          selectedTopConditions,
                        );
                      }}
                      selectedTopConditions={
                        pciDashboardFiltersContext.selectedTopConditions
                      }
                      setSelectedTopConditions={
                        pciDashboardFiltersContext.setSelectedTopConditions
                      }
                      testId="pci-dashboard__filters--top-conditions"
                      title="Conditions Search"
                    />
                  )}
              </div>
              <div className="row">
                <div className="col-md-10 mt-3">
                  {pciDashboardFiltersContext.scenarioTypes.length > 0 && (
                    <PciDashboardScenarioFilters
                      hasFilterData={hasFilterData}
                    />
                  )}
                </div>
                <div className="col-md-2 mt-3 d-flex align-items-end justify-content-end">
                  <div>
                    <QESButton
                      disabled={isApplyDisabled() || !hasFilterData}
                      qesTestId="pci-dashboard__filters--apply"
                      qesType="secondary"
                      onClick={() => onApplyFiltersClick()}
                      className="pb-0"
                    >
                      Apply
                    </QESButton>

                    <QESButton
                      disabled={isClearDisabled() || !hasFilterData}
                      qesTestId="pci-dashboard__filters--clear"
                      qesType="secondary-danger"
                      onClick={() =>
                        pciDashboardFiltersContext.onClearFiltersClick(
                          props.showLineOfBusiness,
                        )
                      }
                      className="pb-0"
                    >
                      Clear
                    </QESButton>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="row">
        {!!pciDashboardFiltersContext?.projectRunConfiguration
          ?.companionProjectRunConfigurationId && (
          <div className="col-3">
            <div className="d-flex align-items-center mb-3">
              <label
                className="label-s me-3 mb-0 d-inline-block"
                htmlFor="pci-dashboard__filters--project-run-configuration-filter"
              >
                Display
              </label>
              <QESSelect
                containerClassName="d-inline-block"
                disabled={props.isLoading}
                testId="pci-dashboard__filters--project-run-configuration-filter"
                hideLabel={true}
                label="Project Run Configuration Filter"
                onChange={(e) =>
                  projectContext.setDualMembershipMode(
                    false,
                    e.target.value as ProjectRunConfigurationType,
                  )
                }
                value={
                  pciDashboardFiltersContext.projectRunConfiguration
                    ?.projectRunConfigurationType || ""
                }
              >
                {getDualMembershipAliases().map((dualMembership, index) => (
                  <option key={index} value={dualMembership.key}>
                    {dualMembership.name}
                  </option>
                ))}
              </QESSelect>
            </div>
          </div>
        )}
      </div>
      <PciDashboardFiltersOverview
        showLineOfBusiness={props.showLineOfBusiness}
        showNewPatientClaims={props.isPca && !!pciDashboardFiltersContext.newPatientClaimsFilters.length}
      />
    </React.Fragment>
  );
};

export default PciDashboardFilters;
